import React, { useMemo, useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { CLIMBING_STYLES } from '@utils/constants.js';
import { SPORT_CLEAN_ASCENT_TYPES, BOULDER_CLEAN_ASCENT_TYPES } from '@utils/gradeUtils.js';
import { formatAscentDate } from '@utils/dateUtils.js';

const CragPerformance = ({ data, climbingStyle }) => {
  const [sortField, setSortField] = useState('cpg');
  const [sortDirection, setSortDirection] = useState('desc');
  const [minAttempts, setMinAttempts] = useState(30);

  const cragStats = useMemo(() => {
    if (!data?.length) return [];

    // Group attempts by crag
    const cragAttempts = data.reduce((acc, attempt) => {
      const cragName = attempt['Crag Name'];
      if (!acc[cragName]) {
        acc[cragName] = {
          attempts: [],
          cleanAttempts: [],
          firstDate: attempt['Ascent Date'],
          lastDate: attempt['Ascent Date'],
          country: attempt['Country'],
          dates: new Set()
        };
      }
      
      acc[cragName].attempts.push(attempt);
      acc[cragName].dates.add(attempt['Ascent Date'].split('T')[0]);  // Store unique dates
      
      // Track clean ascents separately using the correct clean ascent types
      const isCleanAscent = climbingStyle === CLIMBING_STYLES.BOULDER ? 
        BOULDER_CLEAN_ASCENT_TYPES.includes(attempt["Ascent Type"]) :
        SPORT_CLEAN_ASCENT_TYPES.includes(attempt["Ascent Type"]);
      
      if (isCleanAscent && attempt.isClean) {
        acc[cragName].cleanAttempts.push(attempt);
      }
      
      // Update date range
      const attemptDate = new Date(attempt['Ascent Date']);
      const firstDate = new Date(acc[cragName].firstDate);
      const lastDate = new Date(acc[cragName].lastDate);
      
      if (attemptDate < firstDate) acc[cragName].firstDate = attempt['Ascent Date'];
      if (attemptDate > lastDate) acc[cragName].lastDate = attempt['Ascent Date'];
      
      return acc;
    }, {});

    // Process each crag's statistics using correct CPG calculation
    const cragMetrics = Object.entries(cragAttempts)
      .map(([cragName, data]) => {
        // Calculate CPG using the correct formula based on climbing style
        const cpg = (() => {
          if (data.cleanAttempts.length === 0) return 0;

          if (climbingStyle === CLIMBING_STYLES.BOULDER) {
            const sum = data.cleanAttempts.reduce((acc, ascent) => {
              if (ascent.vgrade === null || isNaN(ascent.vgrade)) return acc;
              return acc + Math.exp(ascent.vgrade);
            }, 0);
            return Math.log(sum);
          } else {
            const sum = data.cleanAttempts.reduce((acc, ascent) => {
              if (ascent.ewbanks === null || isNaN(ascent.ewbanks)) return acc;
              return acc + Math.pow(2, ascent.ewbanks);
            }, 0);
            return Math.log2(sum);
          }
        })();
        
        // Get total unique days
        const totalDays = data.dates.size;
        
        // Calculate CPG per day using the correct exponential formula
        const cpgPerDay = (() => {
          if (cpg === 0 || totalDays === 0) return 0;
          
          if (climbingStyle === CLIMBING_STYLES.BOULDER) {
            return Math.log(Math.pow(Math.E, cpg) / totalDays);
          } else {
            return Math.log2(Math.pow(2, cpg) / totalDays);
          }
        })();
        
        // Calculate success rate
        const successRate = (data.cleanAttempts.length / data.attempts.length) * 100;

        return {
          cragName,
          country: data.country,
          cpg: isNaN(cpg) ? 0 : cpg,
          cpgPerDay: isNaN(cpgPerDay) ? 0 : cpgPerDay,
          cleanSends: data.cleanAttempts.length,
          totalAttempts: data.attempts.length,
          successRate,
          daysAtCrag: totalDays,
          dateRange: {
            start: data.firstDate,
            end: data.lastDate
          }
        };
      })
      .filter(crag => crag.totalAttempts >= minAttempts);

    return cragMetrics.sort((a, b) => {
      const direction = sortDirection === 'desc' ? -1 : 1;
      return direction * (a[sortField] - b[sortField]);
    });
  }, [data, sortField, sortDirection, minAttempts, climbingStyle]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'desc' ? 
      <ChevronDownIcon className="inline w-4 h-4" /> : 
      <ChevronUpIcon className="inline w-4 h-4" />;
  };

  const formatCPG = (value) => {
    if (value === undefined || value === null) return 'N/A';
    if (typeof value !== 'number') return value;
    if (climbingStyle === CLIMBING_STYLES.BOULDER) {
      return `V${value.toFixed(1)}`;
    }
    return value.toFixed(2);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <label className="flex items-center space-x-2">
            <span className="text-sm font-medium">Minimum attempts:</span>
            <select
              className="px-2 py-1 border border-gray-300 rounded"
              value={minAttempts}
              onChange={(e) => setMinAttempts(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>
      </div>
      <div className="overflow-auto flex-1">
        <table className="min-w-full table-auto">
          <thead className="sticky top-0 bg-white shadow-sm">
            <tr className="bg-gray-50">
              <th className="px-4 py-2 text-left">Crag</th>
              <th className="px-4 py-2 text-left">Country</th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('cpg')}>
                CPG <SortIcon field="cpg" />
              </th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('cpgPerDay')}>
                CPG/Day <SortIcon field="cpgPerDay" />
              </th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('cleanSends')}>
                Clean Sends <SortIcon field="cleanSends" />
              </th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('totalAttempts')}>
                Total Attempts <SortIcon field="totalAttempts" />
              </th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('successRate')}>
                Success Rate <SortIcon field="successRate" />
              </th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => handleSort('daysAtCrag')}>
                Days <SortIcon field="daysAtCrag" />
              </th>
              <th className="px-4 py-2">Date Range</th>
            </tr>
          </thead>
          <tbody>
            {cragStats.map((crag, index) => (
              <tr 
                key={crag.cragName}
                className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
              >
                <td className="px-4 py-2 font-medium">{crag.cragName}</td>
                <td className="px-4 py-2">{crag.country}</td>
                <td className="px-4 py-2">{formatCPG(crag.cpg)}</td>
                <td className="px-4 py-2">{formatCPG(crag.cpgPerDay)}</td>
                <td className="px-4 py-2">{crag.cleanSends}</td>
                <td className="px-4 py-2">{crag.totalAttempts}</td>
                <td className="px-4 py-2">{crag.successRate.toFixed(1)}%</td>
                <td className="px-4 py-2">{crag.daysAtCrag}</td>
                <td className="px-4 py-2 text-sm">
                  {formatAscentDate(crag.dateRange.start)} - {formatAscentDate(crag.dateRange.end)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CragPerformance;